import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Phone, Check } from "lucide-react";
import { clubs, contacts } from "@/data/clubs";
import { waHref } from "@/lib/contact";
import { Reveal } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Field = ({ label, children }) => (
  <label className="block">
    <span className="font-medium text-sm text-ink/70">{label}</span>
    <div className="mt-2">{children}</div>
  </label>
);

const inputCls =
  "w-full bg-white border border-ink/15 rounded-xl px-4 py-3 text-ink outline-none transition-colors focus:border-accent focus:ring-2 focus:ring-accent/20 placeholder:text-ink/30";

export const RegistrationForm = ({ defaultClubs = [], schoolClubs = clubs, schoolName = "", regUrl = "" }) => {
  const [form, setForm] = useState({
    parent_name: "",
    phone: "",
    child_name: "",
    grade: "",
    message: "",
  });
  const [selected, setSelected] = useState(defaultClubs);
  const [trial, setTrial] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const toggleClub = (name) =>
    setSelected((s) => (s.includes(name) ? s.filter((x) => x !== name) : [...s, name]));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.parent_name.trim() || !form.phone.trim()) {
      toast.error("נא למלא שם וטלפון");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/registrations`, {
        ...form,
        clubs: selected,
        trial_lesson: trial,
        callback: true,
        school: schoolName,
      });
      setDone(true);
      toast.success("קיבלנו את הפרטים! נחזור אליכם בהקדם");
    } catch (err) {
      toast.error("אירעה שגיאה, נסו שוב או צרו קשר טלפוני");
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setDone(false);
    setForm({ parent_name: "", phone: "", child_name: "", grade: "", message: "" });
    setSelected(defaultClubs);
    setTrial(false);
  };

  return (
    <section id="register" className="px-5 md:px-10 py-20 md:py-32 bg-paper">
      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
        <div className="lg:col-span-5">
          <Reveal>
            <p className="font-head font-semibold text-accent mb-3">מצטרפים למשפחת החוגים</p>
            <h2 className="font-head font-black text-4xl md:text-6xl tracking-tighter leading-none">
              מעוניין שיחזרו אליי
            </h2>
            <p className="mt-6 text-lg text-ink/60 leading-relaxed max-w-md">
              משאירים פרטים ואנחנו חוזרים אליכם עם כל המידע לפרטים נוספים ולשריון מקום.
              ניתן להירשם גם ל<span className="text-accent font-semibold">שיעור ניסיון</span> דרך הטופס.
            </p>

            {regUrl && (
              <a
                href={regUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-6 flex items-center justify-center gap-2 w-full font-head font-semibold bg-accent text-white py-3.5 rounded-xl hover:bg-accent-600 transition-colors"
                data-testid="council-register-link"
              >
                להרשמה הרשמית באתר המועצה ←
              </a>
            )}

            <div className="mt-10 space-y-3">
              {contacts.map((c) => (
                <a
                  key={c.phone}
                  href={waHref(c.phone, "היי, אשמח לפרטים על החוגים")}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-4 p-4 rounded-xl border border-ink/10 bg-white hover:border-accent transition-colors group"
                  data-testid={`contact-${c.phone}`}
                >
                  <span className="grid place-items-center w-11 h-11 rounded-full bg-accent/10 text-accent group-hover:bg-accent group-hover:text-white transition-colors">
                    <Phone className="w-5 h-5" />
                  </span>
                  <div>
                    <div className="font-head font-bold">{c.name}</div>
                    <div className="text-ink/60 tabular-nums" dir="ltr">{c.phone}</div>
                  </div>
                </a>
              ))}
            </div>
          </Reveal>
        </div>

        <div className="lg:col-span-7">
          <Reveal delay={0.1}>
            {done ? (
              <motion.div
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                className="rounded-2xl border border-accent/30 bg-white p-10 md:p-14 text-center"
                data-testid="registration-success"
              >
                <span className="grid place-items-center w-16 h-16 rounded-full bg-accent text-white mx-auto">
                  <Check className="w-8 h-8" />
                </span>
                <h3 className="font-head font-black text-3xl mt-6">תודה! קיבלנו את הפרטים</h3>
                <p className="mt-3 text-ink/60 text-lg">נחזור אליכם בהקדם עם כל המידע.</p>
                <button onClick={reset} className="mt-8 font-head font-semibold text-accent link-underline" data-testid="register-again">
                  שליחת פנייה נוספת
                </button>
              </motion.div>
            ) : (
              <form
                onSubmit={submit}
                className="rounded-2xl border border-ink/10 bg-white p-6 md:p-10 grid grid-cols-1 md:grid-cols-2 gap-5"
                data-testid="registration-form"
              >
                <Field label="שם ההורה *">
                  <input className={inputCls} value={form.parent_name} onChange={set("parent_name")} placeholder="שם מלא" data-testid="input-parent-name" />
                </Field>
                <Field label="טלפון *">
                  <input className={inputCls} value={form.phone} onChange={set("phone")} placeholder="050-0000000" dir="ltr" data-testid="input-phone" />
                </Field>
                <Field label="שם הילד/ה">
                  <input className={inputCls} value={form.child_name} onChange={set("child_name")} placeholder="שם הילד/ה" data-testid="input-child-name" />
                </Field>
                <Field label="כיתה">
                  <select className={inputCls} value={form.grade} onChange={set("grade")} data-testid="input-grade">
                    <option value="">בחר/י כיתה</option>
                    {["א'", "ב'", "ג'", "ד'", "ה'", "ו'"].map((g) => (
                      <option key={g} value={g}>{g}</option>
                    ))}
                  </select>
                </Field>

                <div className="md:col-span-2">
                  <span className="font-medium text-sm text-ink/70">חוגים רלוונטיים (ניתן לבחור כמה)</span>
                  <div className="mt-3 flex flex-wrap gap-2" data-testid="clubs-multiselect">
                    {schoolClubs.map((c) => {
                      const active = selected.includes(c.name);
                      return (
                        <button
                          key={c.id}
                          type="button"
                          onClick={() => toggleClub(c.name)}
                          className={`px-4 py-2 rounded-full text-sm font-semibold border transition-colors ${
                            active
                              ? "bg-accent text-white border-accent"
                              : "bg-white text-ink/70 border-ink/15 hover:border-accent"
                          }`}
                          data-testid={`club-chip-${c.id}`}
                          aria-pressed={active}
                        >
                          {c.name}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="md:col-span-2">
                  <label
                    className="flex items-center gap-3 p-4 rounded-xl border border-ink/15 bg-ink/[0.02] cursor-pointer hover:border-accent transition-colors"
                    data-testid="trial-toggle"
                  >
                    <input
                      type="checkbox"
                      checked={trial}
                      onChange={(e) => setTrial(e.target.checked)}
                      className="w-5 h-5 accent-[#4f46e5]"
                      data-testid="input-trial"
                    />
                    <span className="font-medium">מעוניין/ת גם בשיעור ניסיון</span>
                  </label>
                </div>

                <div className="md:col-span-2">
                  <Field label="הערות">
                    <textarea className={`${inputCls} resize-none`} rows={3} value={form.message} onChange={set("message")} placeholder="שאלה או בקשה מיוחדת" data-testid="input-message" />
                  </Field>
                </div>

                <div className="md:col-span-2">
                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full font-head font-semibold bg-accent text-white py-4 rounded-xl hover:bg-accent-600 transition-colors disabled:opacity-60"
                    data-testid="submit-registration"
                  >
                    {loading ? "שולח..." : "מעוניין שיחזרו אליי לפרטים נוספים"}
                  </button>
                </div>
              </form>
            )}
          </Reveal>
        </div>
      </div>
    </section>
  );
};
